import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAlignLeft,
  faCalendar,
  faCertificate,
  faCircleCheck,
  faCircleQuestion,
  faClipboardCheck,
  faFileLines,
  faHashtag,
  faInputText,
  faList,
  faListCheck,
  faRulerCombined,
} from '@fortawesome/pro-light-svg-icons';
import type { BuilderAnswerType } from '@/api/questionnaires/types';
import { BUILDER_ANSWER_TYPES } from '../-types';

/** Display metadata for every answer type the builder can insert.
 *
 *  - Drives the Question Library's Custom group.
 *  - The first five entries are the v2-native Pascal-case types; the
 *    remaining six keep the legacy ALL_CAPS keys so the response page
 *    on the old frontend renders them correctly.
 *  - Order matches the old frontend's Custom section. */
export interface AnswerTypeMeta {
  type: BuilderAnswerType;
  /** Translation key under `builder.answerTypes.*`. */
  labelKey: string;
  /** Font Awesome icon. */
  icon: IconDefinition;
}

export const ANSWER_TYPE_CATALOG: AnswerTypeMeta[] = [
  {
    type: BUILDER_ANSWER_TYPES.SHORT_TEXT,
    labelKey: 'builder.answerTypes.shortText',
    icon: faInputText,
  },
  {
    type: BUILDER_ANSWER_TYPES.LONG_TEXT,
    labelKey: 'builder.answerTypes.longText',
    icon: faAlignLeft,
  },
  {
    type: BUILDER_ANSWER_TYPES.NUMBER,
    labelKey: 'builder.answerTypes.number',
    icon: faHashtag,
  },
  {
    type: BUILDER_ANSWER_TYPES.YES_OR_NO,
    labelKey: 'builder.answerTypes.yesOrNo',
    icon: faCircleCheck,
  },
  {
    type: BUILDER_ANSWER_TYPES.SELECT,
    labelKey: 'builder.answerTypes.select',
    icon: faList,
  },
  {
    type: BUILDER_ANSWER_TYPES.NUMBER_UOM,
    labelKey: 'builder.answerTypes.numberUom',
    icon: faRulerCombined,
  },
  {
    type: BUILDER_ANSWER_TYPES.CHECK_BOXES,
    labelKey: 'builder.answerTypes.multipleChoice',
    icon: faListCheck,
  },
  {
    type: BUILDER_ANSWER_TYPES.DATE,
    labelKey: 'builder.answerTypes.date',
    icon: faCalendar,
  },
  {
    type: BUILDER_ANSWER_TYPES.DOCUMENTS,
    labelKey: 'builder.answerTypes.documents',
    icon: faFileLines,
  },
  {
    type: BUILDER_ANSWER_TYPES.AUDIT,
    labelKey: 'builder.answerTypes.audit',
    icon: faClipboardCheck,
  },
  {
    type: BUILDER_ANSWER_TYPES.CERTIFICATE,
    labelKey: 'builder.answerTypes.certificate',
    icon: faCertificate,
  },
];

/** Quick lookup from type to its translation key. Used by the question
 *  card to render the "Type: ..." pill.
 *
 *  When `isTemplateBound` is true (the question is sourced from a
 *  Retraced template via `retracedDatapointId`) and the answer type
 *  isn't one of the user-selectable catalog entries, returns
 *  `builder.answerTypes.template` — the template owns the answer
 *  shape, so "Template" reads better than "Unknown type". Template TABLE
 *  questions say "Table": the card shows their column format, so the
 *  badge names it. TABLE stays out of the catalog — authors can't insert
 *  a custom table. */
export function answerTypeLabelKey(
  type: string,
  isTemplateBound = false,
): string {
  const meta = ANSWER_TYPE_CATALOG.find(m => m.type === type);
  if (meta) return meta.labelKey;
  if (!isTemplateBound) return 'builder.answerTypes.unknown';
  return type === BUILDER_ANSWER_TYPES.TABLE
    ? 'builder.answerTypes.table'
    : 'builder.answerTypes.template';
}

/** Quick lookup from type to its Font Awesome icon. */
export function answerTypeIcon(type: string): IconDefinition {
  const meta = ANSWER_TYPE_CATALOG.find(m => m.type === type);
  return meta?.icon ?? faCircleQuestion;
}

/** Maps the server's `fieldTypeId` strings (returned by
 *  `data-points?groupByTemplates=true`) to our BuilderAnswerType. Types
 *  not in this map are dropped from the library. */
export const FIELD_TYPE_TO_ANSWER_TYPE: Record<string, BuilderAnswerType> = {
  // Text-like
  SHORT_TEXT: BUILDER_ANSWER_TYPES.SHORT_TEXT,
  STRING: BUILDER_ANSWER_TYPES.SHORT_TEXT,
  ADDRESS: BUILDER_ANSWER_TYPES.SHORT_TEXT,
  CUSTOM_IDENTIFIER: BUILDER_ANSWER_TYPES.SHORT_TEXT,
  LONG_TEXT: BUILDER_ANSWER_TYPES.LONG_TEXT,
  // Multi-row dynamic table — the preview renders a real cell-edited
  // mini-table driven by the datapoint's `meta.columns`.
  TABLE: BUILDER_ANSWER_TYPES.TABLE,

  // Boolean-like
  YES_NO: BUILDER_ANSWER_TYPES.YES_OR_NO,
  BOOLEAN: BUILDER_ANSWER_TYPES.YES_OR_NO,
  TOGGLE: BUILDER_ANSWER_TYPES.YES_OR_NO,

  // Numbers
  NUMBER: BUILDER_ANSWER_TYPES.NUMBER,
  // Legacy currency / year-bearing number variants degrade to plain
  // number-with-unit for now — the builder doesn't expose those types
  // yet but old questionnaires can still load.
  NUMBER_UOM: BUILDER_ANSWER_TYPES.NUMBER_UOM,
  NUMBER_DYNAMIC_UOM: BUILDER_ANSWER_TYPES.NUMBER_UOM,
  NUMBER_DYNAMIC_CURRENCY: BUILDER_ANSWER_TYPES.NUMBER_UOM,
  NUMBER_DYNAMIC_UOM_WITH_YEAR: BUILDER_ANSWER_TYPES.NUMBER_UOM,
  NUMBER_DYNAMIC_CURRENCY_WITH_YEAR: BUILDER_ANSWER_TYPES.NUMBER_UOM,

  // Dates
  DATE: BUILDER_ANSWER_TYPES.DATE,
  SELECT_YEAR: BUILDER_ANSWER_TYPES.DATE,

  // Choice (single-pick)
  SELECT: BUILDER_ANSWER_TYPES.SELECT,
  SELECT_WITH_FOLLOW_UP: BUILDER_ANSWER_TYPES.SELECT,

  // Choice (multi-pick) — every multi-select variant collapses onto the
  // checkbox preview.
  //
  // `CHECK_BOX` (singular) belongs here, not with the boolean-like types. It is
  // the wire type for a real multi-option checkbox question — 1424 of them
  // carry three or more options — and the old dashboard has always routed
  // CHECK_BOX, CHECK_BOXES and MULTIPLE_CHOICE to the same checkbox component
  // (see AnswerType.jsx). Mapping it onto YES_OR_NO hid every option behind a
  // hardcoded Yes/No pair and made the responder serialize the answer as a
  // boolean, which the datapoint validator then rejected with
  // 400 `invalid_answer_for_datapoint` — and, on custom questions where that
  // validation is skipped, stored a boolean that crashed the old dashboard's
  // `answer?.map(...)`. See BI-1749.
  CHECK_BOX: BUILDER_ANSWER_TYPES.CHECK_BOXES,
  CHECK_BOXES: BUILDER_ANSWER_TYPES.CHECK_BOXES,
  MULTIPLE_CHECK_BOXES: BUILDER_ANSWER_TYPES.CHECK_BOXES,
  MULTIPLE_CHOICE: BUILDER_ANSWER_TYPES.CHECK_BOXES,
  MULTIPLE_SELECT: BUILDER_ANSWER_TYPES.CHECK_BOXES,
  MULTI_SELECT: BUILDER_ANSWER_TYPES.CHECK_BOXES,

  // File / asset uploads
  DOCUMENTS: BUILDER_ANSWER_TYPES.DOCUMENTS,
  FILE_UPLOAD: BUILDER_ANSWER_TYPES.DOCUMENTS,
  // A plain image upload — NOT a company-document/asset link. Routes to the
  // image-upload editor in the responder instead of the asset-link selector.
  MULTIPLE_PHOTO_UPLOAD: BUILDER_ANSWER_TYPES.PHOTO_UPLOAD,
  // Single video upload — NOT a company-document/asset link. Routes to the
  // video-upload editor in the responder instead of the asset-link selector.
  VIDEO: BUILDER_ANSWER_TYPES.VIDEO,
  VIDEO_UPLOAD: BUILDER_ANSWER_TYPES.VIDEO,
  AUDIT: BUILDER_ANSWER_TYPES.AUDIT,
  CERTIFICATE: BUILDER_ANSWER_TYPES.CERTIFICATE,
};
